import { z } from 'zod';
import {
  CONTENT_META_DESCRIPTION_MAX_LENGTH,
  CONTENT_META_DESCRIPTION_MIN_LENGTH,
  CONTENT_TITLE_MAX_LENGTH,
  ContentImageSchema,
  FaqItemSchema,
  RelatedLinkSchema,
  type LandingPageFrontmatter,
  type LandingPageFrontmatterInput,
} from './landing-content';

export * from './landing-content';

/**
 * Content publishing engine schemas and brand validation rules (#1203).
 *
 * Governs guides, blog posts, and marketing editorial content.
 * Enforces SEO frontmatter constraints, Ethereal visual metadata,
 * and PureBox brand copy guardrails.
 */

export const CONTENT_CATEGORIES = [
  'cleanup',
  'security',
  'tools',
  'ai',
  'productivity',
  'comparison',
] as const;

export type ContentCategory = (typeof CONTENT_CATEGORIES)[number];
export const ContentCategorySchema = z.enum(CONTENT_CATEGORIES);

export const GuideFrontmatterSchema = z.object({
  title: z
    .string()
    .trim()
    .min(5, 'Title must be at least 5 characters.')
    .max(CONTENT_TITLE_MAX_LENGTH, `Title must be at most ${CONTENT_TITLE_MAX_LENGTH} characters.`),
  metaDescription: z
    .string()
    .trim()
    .min(
      CONTENT_META_DESCRIPTION_MIN_LENGTH,
      `Meta description must be at least ${CONTENT_META_DESCRIPTION_MIN_LENGTH} characters.`,
    )
    .max(
      CONTENT_META_DESCRIPTION_MAX_LENGTH,
      `Meta description must be at most ${CONTENT_META_DESCRIPTION_MAX_LENGTH} characters.`,
    ),
  headline: z.string().trim().min(5).optional(),
  tldr: z.string().trim().min(10, 'TL;DR is required and must be at least 10 characters.'),
  category: ContentCategorySchema,
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens (kebab-case).'),
  canonicalUrl: z.string().trim().optional(),
  datePublished: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'datePublished must be in YYYY-MM-DD format.'),
  dateModified: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'dateModified must be in YYYY-MM-DD format.')
    .optional(),
  author: z.string().trim().default('PureBox Editorial'),
  readingTime: z.number().int().positive().optional(),
  image: ContentImageSchema.optional(),
  faqs: z.array(FaqItemSchema).min(2, 'At least 2 FAQs are required.'),
  relatedLinks: z.array(RelatedLinkSchema).default([]),
  /** Emits SoftwareApplication JSON-LD in addition to Article. Used by the MCP guide. */
  includeSoftwareApplication: z.boolean().default(false),
  /** Optional warning shown above the body. Not a 1px-bordered callout. */
  warningCallout: z.string().trim().min(10).max(400).optional(),
});

export type GuideFrontmatter = z.infer<typeof GuideFrontmatterSchema>;
export type GuideFrontmatterInput = z.input<typeof GuideFrontmatterSchema>;

export type BrandValidatableFrontmatter =
  | GuideFrontmatter
  | GuideFrontmatterInput
  | LandingPageFrontmatter
  | LandingPageFrontmatterInput;

export interface BrandRuleViolation {
  rule: string;
  message: string;
  match?: string;
}

/**
 * Validates brand, copy, and classification rules across guide and landing content:
 * 1. Disallows "review first" / "review-first" in headlines, titles, and CTAs.
 * 2. Disallows "inbox zero" claims and guaranteed time savings.
 * 3. Enforces canonical track names (Inbox, Archive, Trash) and bans "buckets"/"folders".
 * 4. Validates conspicuous AI disclosure when synthetic imagery is flagged.
 * 5. Prevents high-stakes records (invoices, receipts, contracts, OTPs) from being shown in Trash in sample previews.
 */
export function validateBrandAndCopyRules(params: {
  frontmatter: BrandValidatableFrontmatter;
  contentMarkdown: string;
}): BrandRuleViolation[] {
  const { frontmatter, contentMarkdown } = params;
  const violations: BrandRuleViolation[] = [];
  const scanText = collectBrandScanText(frontmatter, contentMarkdown);

  const reviewFirstMatch = scanText.match(/review[- ]first/i);
  if (reviewFirstMatch) {
    const inCta = /\[[^\]]*review[- ]first[^\]]*\]/i.test(scanText);
    violations.push({
      rule: inCta ? 'no-review-first-in-cta' : 'no-review-first-in-headline',
      message: 'Disallows "review first" in titles, headlines, CTAs, FAQs, and body. Enforces value-driven hooks and "You approve what moves".',
      match: zeroMatchSnippet(scanText, reviewFirstMatch.index ?? 0),
    });
  }

  const zeroClaimMatch = scanText.match(/\binbox[\s-]*zero\b/i);
  if (zeroClaimMatch) {
    violations.push({
      rule: 'no-inbox-zero-claims',
      message: 'Disallows "inbox zero" claims. Favor steady progress, visible control, and calm triage.',
      match: zeroMatchSnippet(scanText, zeroClaimMatch.index ?? 0),
    });
  }

  const guaranteedTimeRegex = /\b(?:guaranteed|guarantees|guarantee)\b[^.\n]{0,60}\b(?:hours|hrs|minutes|mins|time)\b/i;
  const guaranteedMatch = scanText.match(guaranteedTimeRegex);
  if (guaranteedMatch) {
    violations.push({
      rule: 'no-guaranteed-time-savings',
      message: 'Disallows guaranteed time savings. Requires "estimated" or "reclaimable" time framing.',
      match: zeroMatchSnippet(scanText, guaranteedMatch.index ?? 0),
    });
  }

  const bannedTrackPatterns = [
    /\bbuckets?\b/i,
    /\b(?:email|mail|message)\s+categor(?:y|ies)\b/i,
    /\b(?:classification|tracks?|purebox|suggestions?)\s+(?:buckets?|folders?|categor(?:y|ies))\b/i,
    /\b(?:trash|archive|inbox)\s+(?:buckets?|folders?|categor(?:y|ies))\b/i,
    /\binto\s+(?:[a-z-]+\s+){0,3}(?:folders?|categor(?:y|ies)|buckets?)\b/i,
  ];
  for (const pattern of bannedTrackPatterns) {
    const bannedTrackMatch = scanText.match(pattern);
    if (bannedTrackMatch) {
      violations.push({
        rule: 'canonical-track-names',
        message: 'Enforces canonical track names (Inbox, Archive, Trash). Bans buckets, categories, and folders as classification targets.',
        match: zeroMatchSnippet(scanText, bannedTrackMatch.index ?? 0),
      });
      break;
    }
  }

  // 4. Conspicuous AI disclosure when AI-generated imagery is declared
  if (frontmatter.image?.aiGenerated) {
    if (!frontmatter.image.aiDisclosure || frontmatter.image.aiDisclosure.trim().length < 5) {
      violations.push({
        rule: 'conspicuous-ai-disclosure',
        message: 'Conspicuous AI disclosure metadata is required when AI-generated imagery is declared.',
      });
    }
    if (!frontmatter.image.alt || frontmatter.image.alt.trim().length < 5) {
      violations.push({
        rule: 'descriptive-ai-alt-text',
        message: 'Descriptive alt text is required for AI-generated visual hooks.',
      });
    }
  }

  // 5. Accurate privacy and legal claims (prevents false advertising / privacy policy contradictions)
  const falsePrivacyPatterns = [
    /\bzero[- ]data[- ]retention\b/i,
    /\bzero[- ]retention\b/i,
    /\binstant\s+data\s+erasure\b/i,
    /\bephemeral\s+processing\s+without\s+persistent\s+storage\b/i,
    /\bnever\s+used\s+to\s+train\s+public\s+or\s+proprietary\s+models\b/i,
    /\bWorkOS\s+AuthKit\b/i,
  ];
  for (const pattern of falsePrivacyPatterns) {
    const falsePrivacyMatch = scanText.match(pattern);
    if (falsePrivacyMatch) {
      violations.push({
        rule: 'accurate-privacy-and-legal-claims',
        message:
          'Disallows inaccurate or overbroad privacy claims (such as zero-retention, instant data erasure, or never training proprietary models) that conflict with /privacy and /subprocessors.',
        match: zeroMatchSnippet(scanText, falsePrivacyMatch.index ?? 0),
      });
      break;
    }
  }

  // 6. Landing page sampleEmails safety check: never show invoices, receipts, contracts, tax, or OTPs in Trash
  if ('sampleEmails' in frontmatter && Array.isArray(frontmatter.sampleEmails)) {
    const protectedRecordRegex = /\b(?:invoice|receipt|contract|agreement|tax|1099|w-2|verification\s+code|otp|two-factor)\b/i;
    for (const sample of frontmatter.sampleEmails) {
      if (sample.track === 'Trash' && protectedRecordRegex.test(`${sample.sender} ${sample.subject}`)) {
        violations.push({
          rule: 'no-protected-records-in-trash-preview',
          message: 'High-stakes records (invoices, receipts, contracts, tax forms, OTPs) must never be shown in the Trash track in sample previews.',
          match: `${sample.sender}: ${sample.subject}`,
        });
      }
    }
  }

  return violations;
}

function collectBrandScanText(
  frontmatter: BrandValidatableFrontmatter,
  contentMarkdown: string,
): string {
  const faqText = (frontmatter.faqs ?? []).flatMap((faq) => [faq.q, faq.a]);
  const linkText = (frontmatter.relatedLinks ?? []).map((link) => link.label);
  const extraLandingText: string[] = [];
  if ('subhead' in frontmatter) {
    if (frontmatter.badge) extraLandingText.push(frontmatter.badge);
    if (frontmatter.highlight) extraLandingText.push(frontmatter.highlight);
    if (frontmatter.subhead) extraLandingText.push(frontmatter.subhead);
    if (frontmatter.primaryCtaLabel) extraLandingText.push(`[${frontmatter.primaryCtaLabel}](/auth/sign-up)`);
    for (const s of frontmatter.sampleEmails ?? []) {
      extraLandingText.push(s.sender, s.subject, s.rationale);
    }
    for (const vp of frontmatter.valueProps ?? []) {
      extraLandingText.push(vp.title, vp.body);
    }
    if (frontmatter.estimatorCta) {
      extraLandingText.push(
        frontmatter.estimatorCta.headline,
        frontmatter.estimatorCta.body,
        `[${frontmatter.estimatorCta.buttonLabel ?? ''}](/estimator)`,
      );
    }
  }
  return [
    frontmatter.title,
    frontmatter.headline,
    'tldr' in frontmatter ? frontmatter.tldr : undefined,
    frontmatter.metaDescription,
    'warningCallout' in frontmatter ? frontmatter.warningCallout : undefined,
    ...extraLandingText,
    ...faqText,
    ...linkText,
    contentMarkdown,
  ]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join('\n');
}

function zeroMatchSnippet(text: string, index: number, radius = 30): string {
  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + radius + 20);
  return (start > 0 ? '...' : '') + text.slice(start, end).replace(/\s+/g, ' ') + (end < text.length ? '...' : '');
}
